import React, { useState, useMemo } from 'react';

// Define an interface for the new payload structure
interface CallPayload {
  lead_name: string;
  phone_number: string;
  desired_car?: string;
  lead_type?: string;
  city: string;
  language: string;
  showroom?: string;
  collection_name: string;
  bot_name?: string;
  bot_intro?: string;
}

interface CallFormProps {
  onSubmit: (payload: CallPayload) => void;
  isLoading: boolean;
}

// Data for Dropdowns
const languages: string[] = ["Arabic", "English"];
const cities: string[] = ["Saudi", "Riyadh", "Jeddah"];
const showrooms: { [key: string]: string[] } = {
  "Saudi": ["Saudi Branch"],
  "Riyadh": ["RYD Al Sahafa Branch"],
  "Jeddah": ["JED Main Branch", "JED North Branch"],
  "Dammam": ["DAM Corniche Branch", "DAM Central"],
  "Khobar": ["KHB Prince Turki St Branch"],
  "": []
};

const collectionNames: string[] = ["Lead"];
const leadTypes: string[] = ["cash", "finance"];

const CallForm: React.FC<CallFormProps> = ({ onSubmit, isLoading }) => {
  // State for all form fields
  const [leadName, setLeadName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [desiredCar, setDesiredCar] = useState('');
  const [leadType, setLeadType] = useState(leadTypes[0]);
  const [city, setCity] = useState(cities[0]);
  const [language, setLanguage] = useState(languages[1]);
  const [showroom, setShowroom] = useState(showrooms[cities[0]][0]);
  const [collectionName, setCollectionName] = useState(collectionNames[0]);
  const [botName, setBotName] = useState('Fathima');
  const [botIntro, setBotIntro] = useState('Hi this is Fathima from Najeeb Alissa Holding Group');

  const [error, setError] = useState('');

  // Available showrooms based on the selected city
  const availableShowrooms = useMemo(() => {
    return showrooms[city] || [];
  }, [city]);

  // Reset showroom when city changes
  useMemo(() => {
    setShowroom(availableShowrooms[0] || '');
  }, [availableShowrooms]);

  // Event Handlers
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!leadName.trim()) return setError('Lead Name is required.');
    if (!phoneNumber.trim()) return setError('Phone Number is required.');
    if (!city) return setError('Please select a City.');
    if (!language) return setError('Please select a Language.');
    if (!collectionName) return setError('Please select a Campaign Name.');

    // Construct Payload
    const payload: CallPayload = {
      lead_name: leadName.trim(),
      phone_number: phoneNumber.trim(),
      desired_car: desiredCar.trim() || undefined,
      lead_type: leadType,
      city: city,
      language: language,
      showroom: showroom || undefined,
      collection_name: collectionName,
      bot_name: botName || undefined,
      bot_intro: botIntro || undefined
    };

    // Submit
    onSubmit(payload);
  };

  // Helper for standard input props
  const getInputProps = (
    value: string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    placeholder: string,
    type: string = 'text',
    required: boolean = false
  ) => ({
    type: type,
    placeholder: placeholder,
    className: "w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500",
    value: value,
    onChange: onChange,
    disabled: isLoading,
    required: required
  });

  // Helper for select props
  const getSelectProps = (
    value: string,
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void,
    required: boolean = false
  ) => ({
    className: "w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white",
    value: value,
    onChange: onChange,
    disabled: isLoading,
    required: required
  });

  return (
    <div className="max-w-3xl w-full mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">Make a Call</h2>
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
        {/* Lead Name */}
        <div className="space-y-1">
          <label htmlFor="leadName" className="block text-sm font-medium text-gray-700">Lead Name *</label>
          <input
            id="leadName"
            {...getInputProps(leadName, (e) => setLeadName(e.target.value), 'e.g., Salman ', 'text', true)}
          />
        </div>

        {/* Phone Number */}
        <div className="space-y-1">
          <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">Phone Number *</label>
          <input
            id="phoneNumber"
            {...getInputProps(phoneNumber, (e) => setPhoneNumber(e.target.value), 'e.g., +966900364651', 'tel', true)}
          />
        </div>

        {/* Desired Car */}
        <div className="space-y-1">
          <label htmlFor="desiredCar" className="block text-sm font-medium text-gray-700">Interested Car</label>
          <input
            id="desiredCar"
            {...getInputProps(desiredCar, (e) => setDesiredCar(e.target.value), 'e.g., Explorer')}
          />
        </div>

        {/* Lead Type */}
        <div className="space-y-1">
          <label htmlFor="leadType" className="block text-sm font-medium text-gray-700">Lead Type</label>
          <select id="leadType" {...getSelectProps(leadType, (e) => setLeadType(e.target.value))}>
            {leadTypes.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        {/* City */}
        {/* <div className="space-y-1">
          <label htmlFor="city" className="block text-sm font-medium text-gray-700">City *</label>
          <select
            id="city"
            {...getSelectProps(city, (e) => setCity(e.target.value), true)}
          >
            {cities.map((cityOption) => (
              <option key={cityOption} value={cityOption}>{cityOption}</option>
            ))}
          </select>
        </div> */}

        {/* Language */}
        <div className="space-y-1">
          <label htmlFor="language" className="block text-sm font-medium text-gray-700">Language *</label>
          <select id="language" {...getSelectProps(language, (e) => setLanguage(e.target.value), true)}>
            {languages.map((lang) => (
              <option key={lang} value={lang}>{lang}</option>
            ))}
          </select>
        </div>

        {/* Showroom */}
        {/* <div className="space-y-1">
          <label htmlFor="showroom" className="block text-sm font-medium text-gray-700">Showroom</label>
          <select id="showroom" {...getSelectProps(showroom, (e) => setShowroom(e.target.value))}>
            {availableShowrooms.map((showroomOption) => (
              <option key={showroomOption} value={showroomOption}>{showroomOption}</option>
            ))}
          </select>
        </div> */}

        {/* Collection Name */}
        <div className="space-y-1">
          <label htmlFor="collectionName" className="block text-sm font-medium text-gray-700">Campaign Name *</label>
          <select id="collectionName" {...getSelectProps(collectionName, (e) => setCollectionName(e.target.value), true)}>
            {collectionNames.map((collection) => (
              <option key={collection} value={collection}>{collection}</option>
            ))}
          </select>
        </div>

        {/* Bot Name */}
        <div className="space-y-1">
          <label htmlFor="botName" className="block text-sm font-medium text-gray-700">Bot Name</label>
          <input
            id="botName"
            {...getInputProps(botName, (e) => setBotName(e.target.value), 'e.g., Fathima')}
          />
        </div>

        {/* Bot Intro */}
        <div className="space-y-1">
          <label htmlFor="botIntro" className="block text-sm font-medium text-gray-700">Bot Introduction</label>
          <input
            id="botIntro"
            {...getInputProps(botIntro, (e) => setBotIntro(e.target.value), 'e.g., Hi this is Fathima from Oorjit')}
          />
        </div>

        {/* Error Message Display */}
        {error && <p className="md:col-span-2 mt-1 text-sm text-red-600 text-center">{error}</p>}

        {/* Submit Button */}
        <div className="md:col-span-2 mt-4">
          <button
            type="submit"
            className="w-full py-2.5 px-4 bg-primary text-white font-semibold rounded-lg hover:bg-blue-700 transition-all duration-300 disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : 'Call the Lead'}
          </button>
        </div>
      </form>
    </div>
  );
};

const LeadCallApp: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [callStatus, setCallStatus] = useState<null | 'success' | 'error'>(null);
  const [callSid, setCallSid] = useState('');
  const [lastError, setLastError] = useState<string | null>(null);

  const makeCall = async (payload: CallPayload) => {
    setIsLoading(true);
    setCallStatus(null);
    setLastError(null);
    console.log("Sending payload:", payload);

    try {
      const response = await fetch('https://pipecat-voice-bot-dev.ispgnet.com/make-lead-qualification-call', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setCallStatus('success');
        setCallSid(data.call_sid || 'N/A');
        console.log("Lead created successfully", data);
      } else {
        console.error('API Error:', response.status, data);
        setCallStatus('error');
        setLastError(data?.message || `API returned status ${response.status}`);
      }
    } catch (error) {
      console.error('Fetch Error:', error);
      setCallStatus('error');
      setLastError(error instanceof Error ? error.message : 'An unknown network error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-3 bg-white p-4 border-b border-gray-200 shadow-sm">
        <h1 className="text-xl font-bold">📲 Lead Management</h1>
      </div>
      <div className="min-h-[calc(100vh-70px)] flex flex-col items-center pt-10 pb-6 bg-gray-100 px-4">
        <CallForm onSubmit={makeCall} isLoading={isLoading} />

        {/* Status Messages */}
        {callStatus === 'success' && (
          <div className="mt-6 p-4 bg-green-100 text-green-800 rounded-lg shadow-md text-center w-full max-w-3xl">
            ✅ Call triggered to the lead successfully!
          </div>
        )}
        {callStatus === 'error' && (
          <div className="mt-6 p-4 bg-red-100 text-red-800 rounded-lg shadow-md text-center w-full max-w-3xl">
            Error creating lead
            {lastError && <span className="text-sm font-medium">Details: {lastError}</span>}
          </div>
        )}
      </div>
    </>
  );
};

export default LeadCallApp;
